import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import DOMPurify from "dompurify";
import {
  AlertTriangle,
  Ban,
  Check,
  Link2,
  RefreshCw,
  RotateCcw,
  Paperclip,
  Send,
  ThumbsDown,
  Trash2,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { FilterMultiSelect } from "@/components/ui/FilterMultiSelect";
import { ManagedFileLink } from "@/components/ui/ManagedFileLink";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { PageLoader } from "@/components/ui/Spinner";
import { LeadRichText } from "@/features/leads/components/LeadRichText";
import { EmptyState, SectionCard } from "@/features/power_projects/components/ui";
import {
  INBOUND_KINDS,
  INBOUND_KIND_COLORS,
  useAssignLead,
  useBlockSender,
  useDeclineLead,
  useDeleteReplyAttachment,
  useInbox,
  useMarkHandled,
  useReclassify,
  useResumeSequence,
  useSaveProposal,
  useSendReply,
  useSetReminder,
  useUploadReplyAttachment,
  type InboundAttachment,
  type InboundMessage,
} from "@/features/leads/api/inbox";
import { useCampaigns } from "@/features/leads/api/campaigns";
import { useLeadDictionaries, useLeads } from "@/features/leads/api/leads";
import { SignatureHint } from "@/features/leads/components/SignatureHint";
import { signatureLines, withPersonalSignature } from "@/features/leads/utils/signature";
import { useAuthStore } from "@/store/auth";
import { uploadErrorMessage } from "@/utils/managedFile";

/**
 * Odkażanie treści od OBCEGO nadawcy przed pokazaniem jej w naszej aplikacji.
 *
 * Lista dozwolonych znaczników jest szersza niż przy komentarzach — firmy
 * przysyłają tabelki, listy i podpisy i chcemy je widzieć takimi, jakie są.
 * Nie ma na niej niczego wykonywalnego: żadnych skryptów, zdarzeń, iframe'ów
 * ani atrybutu style. Obrazy zostają, ale tylko z adresu — bez `srcset`,
 * którym da się obejść filtrowanie.
 */
function sanitizeIncoming(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      "p", "br", "div", "span", "strong", "b", "em", "i", "u", "s",
      "ul", "ol", "li", "h1", "h2", "h3", "h4", "blockquote", "pre", "code",
      "table", "thead", "tbody", "tr", "td", "th", "hr", "a", "img",
    ],
    ALLOWED_ATTR: ["href", "title", "alt", "src", "colspan", "rowspan", "start"],
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|cid:|data:image\/(?:png|jpe?g|gif|webp))/i,
  });
}

/**
 * Skrzynka zakupy@ — odpowiedzi firm na nasze zapytania.
 *
 * Układ dwukolumnowy, bo to praca „przejrzyj i odpowiedz": po lewej co czeka,
 * po prawej jedna rozmowa w całości. Propozycja odpowiedzi jest edytowalna
 * i NIC nie wychodzi bez kliknięcia „Wyślij" — pisze do nas obca firma,
 * a automatyczna odpowiedź na cudzy tekst to proszenie się o kłopoty.
 */
export function InboxPage() {
  // ?id= pozwala wejść tu prosto z osi czasu firmy, na konkretną wiadomość.
  // Wtedy z automatu pokazujemy też obsłużone — inaczej link do wątku, na który
  // ktoś już odpisał, prowadziłby na pustą listę.
  // ?campaign= także siedzi w adresie: filtr przeżywa F5 i da się podlinkować
  // skrzynkę jednej kampanii z jej karty.
  const [params, setParams] = useSearchParams();
  const fromUrl = params.get("id");
  const campaign = params.get("campaign") ?? "";
  // Rozpoznanie trzymamy w adresie w tej samej formie, w jakiej idzie do API:
  // kilka wartości po przecinku. Kilka, bo najczęstsza potrzeba to nie „pokaż
  // odbicia", tylko „schowaj odbicia" — a to znaczy wybrać całą resztę.
  const kindParam = params.get("kind") ?? "";
  const kinds = useMemo(() => kindParam.split(",").filter(Boolean), [kindParam]);
  const [showAll, setShowAll] = useState(!!fromUrl);
  const { data: messages, isLoading, isFetching, refetch } = useInbox({
    all: showAll,
    campaign: campaign || undefined,
    kind: kindParam || undefined,
  });
  const { data: campaigns } = useCampaigns();
  const [selectedId, setSelectedId] = useState<string | null>(fromUrl);

  // Kliknięcie w powiadomienie „firma odpisała", gdy skrzynka JEST już otwarta,
  // podmienia tylko ?id= w adresie — strona się nie montuje od nowa, więc stan
  // z useState(fromUrl) zostałby przy poprzedniej wiadomości i nic by się nie
  // zmieniło. Każda zmiana parametru musi więc przestawić zaznaczenie (i pokazać
  // obsłużone, tak jak przy wejściu z linku).
  useEffect(() => {
    if (!fromUrl) return;
    setSelectedId(fromUrl);
    setShowAll(true);
  }, [fromUrl]);

  const rows = messages ?? [];

  // Powiadomienie potrafi wyprzedzić listę odświeżaną co minutę: wskazanej
  // wiadomości może jeszcze nie być w pamięci. Wtedy dociągamy listę od razu,
  // zamiast pokazywać pierwszą z brzegu (fallback niżej) i udawać, że to ta.
  // Jedno dociągnięcie na adres — nieistniejąca wiadomość (np. skasowana)
  // kręciłaby inaczej pętlę pobrań.
  const missingFromUrl = !!fromUrl && !isFetching && !rows.some((row) => row.id === fromUrl);
  const refetchedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!missingFromUrl || refetchedFor.current === fromUrl) return;
    refetchedFor.current = fromUrl;
    void refetch();
  }, [fromUrl, missingFromUrl, refetch]);

  const selected = useMemo(
    () => rows.find((row) => row.id === selectedId) ?? rows[0] ?? null,
    [rows, selectedId],
  );

  const campaignOptions = useMemo(
    () => (campaigns ?? []).map((row) => ({ value: row.id, label: row.name })),
    [campaigns],
  );

  const updateParams = (patch: Record<string, string | null>) =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(patch)) {
          if (value) next.set(key, value);
          else next.delete(key);
        }
        return next;
      },
      { replace: true },
    );

  if (isLoading) return <PageLoader />;

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[22rem_1fr]">
      {/* Kolumna listy przewija się osobno i trzyma się góry ekranu: przy
          długiej rozmowie po prawej lista nie ucieka poza ekran, a przy długiej
          liście rozmowa nie znika za nią. Wysokość zostawia zapas na pasek
          górny i ewentualny baner. Na wąskim ekranie kolumny są jedna pod
          drugą i osobne przewijanie tylko by przeszkadzało. */}
      <div className="flex flex-col lg:sticky lg:top-0 lg:max-h-[calc(100dvh-8rem)] lg:self-start">
        <div className="mb-2 flex shrink-0 items-center justify-between">
          <span className="section-label">
            {showAll ? "Wszystkie wiadomości" : "Czeka na odpowiedź"}
          </span>
          <button
            type="button"
            className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
            onClick={() => setShowAll((v) => !v)}
          >
            {showAll ? "pokaż tylko oczekujące" : "pokaż obsłużone"}
          </button>
        </div>

        <div className="mb-2 shrink-0">
          <SearchableSelect
            aria-label="Kampania"
            options={campaignOptions}
            value={campaign}
            placeholder="Wszystkie kampanie"
            onChange={(e) => updateParams({ campaign: e.target.value, id: null })}
          />
        </div>

        {/* Zmiana filtra kasuje ?id= z adresu: wskazana wiadomość może do niego
            nie pasować, a wtedy link trzymałby zaznaczenie na czymś, czego na
            liście nie widać. */}
        <div className="mb-2 flex shrink-0 items-center gap-2">
          <FilterMultiSelect
            label="Rozpoznanie"
            options={INBOUND_KINDS}
            selected={kinds}
            onChange={(next) => updateParams({ kind: next.join(","), id: null })}
          />
          {kinds.length > 0 && (
            <button
              type="button"
              className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
              onClick={() => updateParams({ kind: null, id: null })}
            >
              wyczyść
            </button>
          )}
        </div>

        {rows.length === 0 ? (
          <EmptyState>
            {kinds.length > 0
              ? "Żadna wiadomość nie ma wybranego rozpoznania. Zmień filtr albo pokaż obsłużone."
              : campaign
                ? "Na maile tej kampanii nikt jeszcze nie odpisał — albo wszystko już obsłużone."
                : showAll
                  ? "Na zakupy@ nic jeszcze nie przyszło."
                  : "Wszystko obsłużone. Nowe odpowiedzi pojawią się tu w ciągu kilku minut."}
          </EmptyState>
        ) : (
          <ul className="min-h-0 flex-1 space-y-1.5 overflow-y-auto lg:pr-1">
            {rows.map((row) => (
              <li key={row.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedId(row.id);
                    if (fromUrl) updateParams({ id: null });
                  }}
                  className={`w-full border p-2.5 text-left ${
                    selected?.id === row.id
                      ? "border-[var(--color-primary)] bg-[var(--color-bg-elevated)]"
                      : "border-[var(--color-border)] hover:border-[var(--color-border-strong)]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="truncate text-[13px] font-medium text-[var(--color-text-primary)]">
                      {row.lead_name || row.from_name || row.from_email}
                    </span>
                    <Badge color={INBOUND_KIND_COLORS[row.kind] ?? "gray"}>
                      {row.kind_label || "?"}
                    </Badge>
                  </div>
                  <div className="mt-0.5 flex items-center gap-1 text-[11px] text-[var(--color-text-muted)]">
                    {/* Spinacz na liście: po nim widać, gdzie szukać przysłanej
                        wyceny, bez otwierania każdej wiadomości po kolei. */}
                    {row.has_attachments && <Paperclip size={11} className="shrink-0" />}
                    <span className="truncate">{row.subject || "(bez tematu)"}</span>
                  </div>
                  <div className="mt-0.5 text-[11px] text-[var(--color-text-muted)]">
                    {format(new Date(row.received_at), "dd.MM.yyyy HH:mm")}
                    {!row.lead && (
                      <span className="ml-2 text-[var(--color-primary)]">
                        niedopasowane
                      </span>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {selected ? <MessagePanel key={selected.id} message={selected} /> : <div />}
    </div>
  );
}

/**
 * Propozycja od modelu przychodzi jako czysty tekst, a edytor pracuje na HTML-u.
 * Bez tego akapity zlewają się w jeden blok i człowiek musi je rozbijać ręcznie.
 */
function asEditorHtml(text: string): string {
  const value = (text || "").trim();
  if (!value) return "";
  if (value.startsWith("<")) return value;
  return value
    .split(/\n{2,}/)
    .map((para) => `<p>${para.replace(/\n/g, "<br>")}</p>`)
    .join("");
}

/** Rozmiar pliku po ludzku — kilobajty przy dokumentach, megabajty przy skanach. */
function formatSize(bytes: number | null): string {
  if (!bytes) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf(".");
  return dot > 0 ? filename.slice(dot).toLowerCase() : "";
}

/**
 * Załączniki, które PRZYSZŁY od firmy — wycena, referencje, skan umowy.
 *
 * Do 05.09.2026 dało się je otworzyć wyłącznie logując się do Outlooka na
 * skrzynkę zakupy@: system pokazywał treść maila i ani słowa o plikach, więc
 * „w załączniku przesyłam ofertę" kończyło się szukaniem po omacku (zgłoszenie
 * dev z 04.09.2026).
 *
 * Wklejone obrazki (logo z podpisu) odsiewamy — na liście robią z jednej wyceny
 * rząd bezimiennych plików. Nazwa pliku jest widoczna ZAWSZE, także gdy nie ma
 * czego kliknąć: plik od obcego nadawcy czeka na skan antywirusowy, a sam
 * komunikat bez nazwy wygląda jak brak załącznika.
 */
function documentAttachments(files: InboundAttachment[]): InboundAttachment[] {
  return (files ?? []).filter(
    (file) => !(file.is_inline && file.content_type.startsWith("image/")),
  );
}

function IncomingAttachments({ message }: { message: InboundMessage }) {
  const files = documentAttachments(message.attachments);
  // Graph powiedział, że załączniki są, a u nas ich nie ma: albo przebieg
  // skrzynki jeszcze do nich nie dotarł, albo pobranie padło. Cisza w tym
  // miejscu byłaby gorsza od komunikatu — user szukałby pliku, którego nie ma.
  const promised = message.has_attachments && files.length === 0;
  if (files.length === 0 && !promised) return null;

  return (
    <div className="mt-3 border-t border-[var(--color-border)] pt-3">
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-[var(--color-text-secondary)]">
        <Paperclip size={12} /> Załączniki od firmy
        {files.length > 0 ? ` (${files.length})` : ""}
      </p>
      {promised ? (
        <p className="text-[11px] text-[var(--color-text-muted)]">
          Wiadomość ma załączniki, ale nie ma ich jeszcze u nas — pobieranie idzie
          w tle razem z odbiorem poczty. Odśwież za kilka minut.
        </p>
      ) : (
        <ul className="space-y-1 text-[13px]">
          {files.map((file) => (
            <li key={file.id} className="flex flex-wrap items-center gap-x-2">
              {file.url ? (
                <ManagedFileLink
                  file={{
                    url: file.url,
                    scan_status: file.scan_status,
                    original_filename: file.name,
                  }}
                >
                  {file.name}
                </ManagedFileLink>
              ) : (
                <>
                  <span className="text-[var(--color-text-secondary)]">
                    {file.name}
                  </span>
                  <ManagedFileLink
                    file={{ url: null, scan_status: file.scan_status }}
                    empty={null}
                  />
                </>
              )}
              <span className="text-[11px] text-[var(--color-text-muted)]">
                {formatSize(file.size_bytes)}
              </span>
              {file.download_error && (
                <Badge color="orange">{file.download_error}</Badge>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Załączniki odpowiedzi — cennik, referencje, wzór umowy dla TEJ jednej firmy.
 *
 * Reguły przyjmowania (rozszerzenia blokowane przez Outlooka odbiorcy, limit
 * rozmiaru i liczby plików) przychodzą ze słowników, więc ekran nie trzyma
 * drugiej ich kopii. Sprawdzamy je mimo to PRZED wgraniem: odbicie pliku po
 * minucie wysyłania boli bardziej niż komunikat od razu.
 *
 * Pliki lecą jeden po drugim, nie równolegle — limit dotyczy całej wiadomości,
 * a wysłane naraz żądania liczyłyby go każde od tego samego stanu.
 */
function ReplyAttachments({ message }: { message: InboundMessage }) {
  const upload = useUploadReplyAttachment();
  const remove = useDeleteReplyAttachment();
  const { data: dictionaries } = useLeadDictionaries();
  const [error, setError] = useState("");

  const files = message.reply_attachments ?? [];
  const rules = dictionaries?.mail_attachments;
  const used = files.reduce((sum, file) => sum + (file.size_bytes ?? 0), 0);
  const limitMb = rules ? Math.round(rules.max_total_bytes / (1024 * 1024)) : null;

  const handle = async (picked: File[]) => {
    setError("");
    let count = files.length;
    let total = used;
    for (const file of picked) {
      const ext = extensionOf(file.name);
      if (rules?.blocked_extensions.includes(ext)) {
        setError(
          `Outlook blokuje pliki ${ext} — odbiorca ich nie otworzy. ` +
            "Spakuj plik do ZIP-a albo wyślij link.",
        );
        return;
      }
      if (rules && count >= rules.max_count) {
        setError(`W jednej wiadomości mieści się ${rules.max_count} załączników.`);
        return;
      }
      if (rules && total + file.size > rules.max_total_bytes) {
        setError(
          `Załączniki przekroczyłyby ${limitMb} MB — taki mail odbija się na ` +
            "serwerach odbiorców.",
        );
        return;
      }
      try {
        await upload.mutateAsync({ id: message.id, file });
      } catch (err) {
        // `errors` is an object for a per-field refusal ({ file: [...] }) — rendering
        // it raw crashed the page into the route's error screen.
        return setError(uploadErrorMessage(err, `Nie udało się wgrać pliku ${file.name}.`));
      }
      count += 1;
      total += file.size;
    }
  };

  const sent = !!message.reply_sent_at;
  if (sent && files.length === 0) return null;

  return (
    <div className="mt-3 border-t border-[var(--color-border)] pt-3">
      <p className="mb-2 flex items-center gap-1.5 text-[11px] font-medium text-[var(--color-text-secondary)]">
        <Paperclip size={12} /> Załączniki{files.length > 0 ? ` (${files.length})` : ""}
      </p>

      {files.length > 0 && (
        <ul className="mb-2 space-y-1 text-[13px]">
          {files.map((file) => (
            <li key={file.id} className="flex items-center justify-between gap-2">
              <span className="min-w-0 truncate">
                <ManagedFileLink
                  file={{
                    url: file.url,
                    scan_status: file.scan_status,
                    original_filename: file.original_filename,
                  }}
                >
                  {file.original_filename}
                </ManagedFileLink>
                <span className="ml-1.5 text-[11px] text-[var(--color-text-muted)]">
                  {formatSize(file.size_bytes)}
                </span>
              </span>
              {!sent && (
                <button
                  type="button"
                  onClick={() => remove.mutate(file.id)}
                  className="shrink-0 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                  title="Usuń załącznik"
                >
                  <Trash2 size={13} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {!sent && (
        <>
          <FileDropZone
            multiple
            onSelect={(picked) => void handle(picked)}
            disabled={upload.isPending}
            label={
              limitMb
                ? `Przeciągnij pliki albo kliknij, żeby wybrać (maks. ${rules?.max_count} plików, łącznie ${limitMb} MB)`
                : "Przeciągnij pliki albo kliknij, żeby wybrać"
            }
          />
          <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
            Outlook odbiorcy nie przepuszcza plików wykonywalnych i skryptów
            (.exe, .js, .bat, .html…) — takie trzeba spakować do ZIP-a.
            {used > 0 && ` Wykorzystane: ${formatSize(used)}.`}
          </p>
          {error && <p className="mt-1 text-xs text-[var(--color-primary)]">{error}</p>}
        </>
      )}
    </div>
  );
}

function MessagePanel({ message }: { message: InboundMessage }) {
  const saveProposal = useSaveProposal();
  const sendReply = useSendReply();
  const markHandled = useMarkHandled();
  const reclassify = useReclassify();
  const resumeSequence = useResumeSequence();
  const setReminder = useSetReminder();

  // Treść startowa = propozycja modelu + podpis osoby, która TERAZ patrzy na
  // wątek. Podpis jest zwykłym tekstem w edytorze: widać go i da się poprawić,
  // a ten sam człowiek nie zostanie podpisany dwa razy (backend sprawdza nazwisko).
  const user = useAuthStore((s) => s.user);
  const lines = useMemo(() => signatureLines(user), [user]);
  const composed = useMemo(
    () => withPersonalSignature(asEditorHtml(message.proposed_reply), lines),
    [message.proposed_reply, lines],
  );
  const [text, setText] = useState(composed);
  const [error, setError] = useState("");
  // DW/UDW: schowane, dopóki ktoś ich nie potrzebuje. Odpowiedź idzie jak
  // „Odpowiedz wszystkim" (nadawca + pozostali adresaci jego maila — buduje to
  // backend przez Graph), więc dodatkowi adresaci to rzadkość, a puste pola nad
  // treścią kuszą do wpisywania czegokolwiek.
  const [copiesOpen, setCopiesOpen] = useState(false);
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  // Pole „za ile dni" startuje od tego, co już ustawiono dla tej firmy — pusty
  // input po każdym wejściu w wątek kusiłby do wpisania czegokolwiek na nowo.
  const [days, setDays] = useState(() => String(message.remind_in_days ?? 45));

  useEffect(() => {
    setText(composed);
    setError("");
    setDays(String(message.remind_in_days ?? 45));
    setCopiesOpen(false);
    setCc("");
    setBcc("");
  }, [message.id, composed, message.remind_in_days]);

  const alreadyReplied = !!message.reply_sent_at;

  return (
    <div className="space-y-3">
      <SectionCard
        title={message.lead_name || message.from_email}
        action={
          message.lead ? (
            <Link
              to={`/leads/${message.lead}`}
              className="text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
            >
              otwórz kartę firmy →
            </Link>
          ) : null
        }
      >
        <div className="space-y-1 text-xs text-[var(--color-text-secondary)]">
          <div>
            <span className="section-label mr-2">Od</span>
            <span className="font-mono">{message.from_email}</span>
          </div>
          <div>
            <span className="section-label mr-2">Temat</span>
            {message.subject || "(bez tematu)"}
          </div>
          {message.campaign_name && (
            <div>
              <span className="section-label mr-2">Kampania</span>
              {message.campaign_name}
            </div>
          )}
        </div>

        {message.body_html ? (
          <div
            className="rich-text-content mt-3 max-h-96 overflow-y-auto border border-[var(--color-border)] bg-white p-3 text-[13px] text-black"
            dangerouslySetInnerHTML={{ __html: sanitizeIncoming(message.body_html) }}
          />
        ) : (
          <pre className="mt-3 max-h-72 overflow-y-auto whitespace-pre-wrap border border-[var(--color-border)] p-3 text-xs">
            {message.body}
          </pre>
        )}

        <IncomingAttachments message={message} />
      </SectionCard>

      {!message.lead && <UnmatchedCard message={message} />}

      <SectionCard title="Rozpoznanie">
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <Badge color={INBOUND_KIND_COLORS[message.kind] ?? "gray"}>
            {message.kind_label || "nierozpoznane"}
          </Badge>
          <span className="text-[var(--color-text-muted)]">
            {message.classification_note}
          </span>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <select
            className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-1.5 py-1 text-[11px]"
            value={message.kind}
            onChange={(e) =>
              reclassify.mutate({ id: message.id, kind: e.target.value })
            }
            title="Ręczna zmiana kategorii — system wykona reakcję właściwą dla nowej"
          >
            <option value="">zmień rozpoznanie…</option>
            {INBOUND_KINDS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
          <Button
            size="sm"
            variant="ghost"
            loading={reclassify.isPending}
            onClick={() => reclassify.mutate({ id: message.id })}
          >
            <RefreshCw size={12} className="mr-1.5 inline" /> Przeanalizuj ponownie
          </Button>
        </div>

        {/* „Nie teraz" — kiedy wrócić. Termin ustawia człowiek dla KAŻDEJ firmy
            osobno: jedna pisze „wracamy za tydzień", inna „w przyszłym roku".
            Zadanie już istnieje (założyła je reakcja na odpowiedź), to je przestawia. */}
        {message.kind === "later" && message.lead && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border border-[var(--color-border)] p-2 text-[11px]">
            <span className="text-[var(--color-text-secondary)]">
              Wrócić do tej firmy za
            </span>
            <input
              type="number"
              min={1}
              max={730}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              className="w-16 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-1.5 py-1 text-[11px]"
            />
            <span className="text-[var(--color-text-secondary)]">dni</span>
            <Button
              size="sm"
              variant="ghost"
              loading={setReminder.isPending}
              disabled={!Number(days)}
              onClick={() =>
                setReminder.mutate({ id: message.id, days: Number(days) })
              }
            >
              Ustaw przypomnienie
            </Button>
            <span className="text-[var(--color-text-muted)]">
              {message.remind_in_days
                ? `teraz: za ${message.remind_in_days} dni`
                : "nikt jeszcze nie ustawił — obowiązuje termin domyślny"}
            </span>
          </div>
        )}

        {/* Autoresponder („jestem na urlopie do 20") przychodzi z adresu firmy i
            zatrzymuje przypomnienia dokładnie tak jak prawdziwa odpowiedź.
            Rozpoznać to może tylko człowiek — i cofnąć stąd, gdzie to przeczytał. */}
        {message.stopped_sequences?.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-2 border border-[var(--color-border)] p-2 text-[11px]">
            <span className="flex-1 text-[var(--color-text-secondary)]">
              Ta odpowiedź zatrzymała przypomnienia w:{" "}
              <b className="text-[var(--color-text-primary)]">
                {message.stopped_sequences.join(", ")}
              </b>
              . Jeśli to był tylko autoresponder, można je wznowić — mail wróci do
              zatwierdzenia, nie wyjdzie sam.
            </span>
            <Button
              size="sm"
              variant="ghost"
              loading={resumeSequence.isPending}
              onClick={() => resumeSequence.mutate(message.id)}
            >
              <RotateCcw size={12} className="mr-1.5 inline" /> Wznów przypomnienia
            </Button>
          </div>
        )}

        {message.actions?.length > 0 && (
          <ul className="mt-3 space-y-0.5 text-[11px] text-[var(--color-text-secondary)]">
            {message.actions.map((line, i) => (
              <li key={i}>· {line}</li>
            ))}
          </ul>
        )}
      </SectionCard>

      <DecisionCard message={message} />

      <SectionCard title={alreadyReplied ? "Wysłana odpowiedź" : "Propozycja odpowiedzi"}>
        {alreadyReplied ? (
          <>
            <p className="text-xs text-[var(--color-text-muted)]">
              Odpowiedziano {format(new Date(message.reply_sent_at!), "dd.MM.yyyy HH:mm")}
              {message.reply_sent_by_name || message.handled_by_name
                ? ` — ${message.reply_sent_by_name || message.handled_by_name}`
                : ""}
              .
            </p>
            {/* Treść tego, co realnie poszło. Bez niej wątek kończył się samym
                stemplem „odpowiedziano" i żeby sprawdzić, co obiecaliśmy, trzeba
                było szukać w historii kontaktu na karcie firmy — a przy
                wiadomości bez firmy nie było tam czego szukać. */}
            {message.reply_body ? (
              <LeadRichText
                content={message.reply_body}
                className="mt-2 max-h-96 overflow-y-auto border border-[var(--color-border)] p-3 text-[13px]"
              />
            ) : (
              <p className="mt-2 text-[11px] text-[var(--color-text-muted)]">
                Treści nie zapisano — ta odpowiedź poszła, zanim zaczęliśmy je
                archiwizować przy wiadomości.
                {message.lead && " Szukaj jej w historii kontaktu na karcie firmy."}
              </p>
            )}
            <ReplyAttachments message={message} />
          </>
        ) : (
          <>
            <p className="mb-2 flex items-start gap-1.5 text-[11px] text-[var(--color-text-muted)]">
              <AlertTriangle size={12} className="mt-0.5 shrink-0" />
              Napisał to model na podstawie treści od obcego nadawcy. Przeczytaj,
              zanim wyślesz — nic nie wychodzi automatycznie.
            </p>
            <RichTextEditor
              value={text}
              onChange={setText}
              placeholder="Brak propozycji — napisz odpowiedź ręcznie."
            />
            <SignatureHint lines={lines} whatGoesOut="odpowiedź pójdzie" />
            <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
              Odpowiedź idzie jak „Odpowiedz wszystkim": do nadawcy i do pozostałych
              adresatów jego wiadomości. Adresy z listy „nie kontaktować" wypadają same.
            </p>
            {copiesOpen ? (
              <div className="mt-2 space-y-2">
                <CopyField
                  label="DW"
                  hint="Widoczne dla wszystkich adresatów."
                  value={cc}
                  onChange={setCc}
                />
                <CopyField
                  label="UDW"
                  hint="Ukryte — adresat główny ich nie zobaczy."
                  value={bcc}
                  onChange={setBcc}
                />
                <p className="text-[11px] text-[var(--color-text-muted)]">
                  Kilka adresów oddziel przecinkiem. To są adresaci DODATKOWI — nadawca
                  i reszta wątku dostają odpowiedź i tak.
                </p>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCopiesOpen(true)}
                className="mt-2 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
              >
                + Dodaj DW / UDW
              </button>
            )}
            <ReplyAttachments message={message} />
            {error && <p className="mt-2 text-xs text-[var(--color-primary)]">{error}</p>}
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  loading={markHandled.isPending}
                  onClick={() => markHandled.mutate(message.id)}
                  title="Zamyka wątek bez wysyłania — np. gdy odpowiedź poszła telefonem"
                >
                  <Check size={12} className="mr-1.5 inline" /> Załatwione bez maila
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={text === composed}
                  loading={saveProposal.isPending}
                  onClick={() => saveProposal.mutate({ id: message.id, text })}
                >
                  Zapisz wersję roboczą
                </Button>
                <Button
                  size="sm"
                  disabled={!text.trim()}
                  loading={sendReply.isPending}
                  onClick={() => {
                    if (
                      !confirm(
                        "Wysłać tę odpowiedź? Trafi do nadawcy i do pozostałych adresatów jego wiadomości (odpowiedz wszystkim).",
                      )
                    )
                      return;
                    setError("");
                    sendReply.mutate(
                      {
                        id: message.id,
                        text,
                        cc: splitAddresses(cc),
                        bcc: splitAddresses(bcc),
                      },
                      {
                        onError: (err) =>
                          setError(
                            (err as { response?: { data?: { errors?: string } } })
                              ?.response?.data?.errors ??
                              "Nie udało się wysłać odpowiedzi.",
                          ),
                      },
                    );
                  }}
                >
                  <Send size={12} className="mr-1.5 inline" /> Wyślij
                </Button>
              </div>
            </div>
          </>
        )}
      </SectionCard>
    </div>
  );
}

/**
 * Werdykt o FIRMIE — nie o wiadomości.
 *
 * Osobna karta i zawsze widoczna, także gdy odpowiedź już poszła: decyzja „nie
 * chcemy z nimi pracować" zapada zwykle właśnie po przeczytaniu wyceny albo
 * referencji, o które sami poprosiliśmy. Wcześniej obie te decyzje siedziały
 * w bloku propozycji odpowiedzi, więc na wątku z wysłaną odpowiedzią nie było
 * do nich dostępu.
 *
 * Dwie decyzje, dwa różne skutki — i dlatego stoją obok siebie z opisem:
 * „nie kontaktować" to sprzeciw FIRMY (wpis na listę globalną, chroni ją przed
 * każdą przyszłą wysyłką), „nie chcemy współpracować" to NASZ werdykt (zostaje
 * przy firmie, zatrzymuje kampanie, nie udaje sprzeciwu, którego nikt nie zgłosił).
 */
function DecisionCard({ message }: { message: InboundMessage }) {
  const blockSender = useBlockSender();
  const declineLead = useDeclineLead();

  const [reasonOpen, setReasonOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");

  const blocked = message.lead_status === "blocked";
  const declined = message.lead_status === "declined";

  const fail = (err: unknown, fallback: string) => {
    const detail = (err as { response?: { data?: { errors?: unknown } } })?.response
      ?.data?.errors;
    setError(typeof detail === "string" ? detail : fallback);
  };

  return (
    <SectionCard title="Decyzja o firmie">
      {declined && (
        <p className="mb-2 border border-[var(--color-border)] p-2 text-[11px] text-[var(--color-text-secondary)]">
          Firma jest oznaczona jako <b>„nie chcemy współpracować”</b>. Kampanie do
          niej stoją; powód i autor decyzji są w historii kontaktu na jej karcie.
        </p>
      )}
      {blocked && (
        <p className="mb-2 border border-[var(--color-border)] p-2 text-[11px] text-[var(--color-text-secondary)]">
          Firma jest na liście <b>„nie kontaktować”</b> — żadna kampania nie wyśle
          do niej maila. Zakaz zdejmuje się z karty firmy.
        </p>
      )}

      {!declined && !blocked && (
        <p className="mb-2 text-[11px] text-[var(--color-text-muted)]">
          <b>Nie chcemy współpracować</b> to nasza decyzja — zostaje przy firmie
          i zatrzymuje kampanie. <b>Nie kontaktować</b> to sprzeciw firmy — trafia
          na listę globalną i chroni ją przed każdą przyszłą wysyłką.
        </p>
      )}

      {reasonOpen ? (
        <div className="space-y-2 border border-[var(--color-border)] p-2">
          <label className="block text-[11px] text-[var(--color-text-secondary)]">
            Dlaczego nie chcemy z nimi pracować? Ten powód przeczyta za pół roku
            ktoś, kto będzie chciał tę decyzję odwrócić.
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError("");
              }}
              rows={3}
              placeholder="np. stawki dwa razy wyższe od rynkowych, brak uprawnień SEP"
              className="mt-1 w-full border border-[var(--color-border)] bg-transparent px-2 py-1 text-xs text-[var(--color-text-primary)]"
            />
          </label>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="danger"
              disabled={!reason.trim()}
              loading={declineLead.isPending}
              onClick={() =>
                declineLead.mutate(
                  { id: message.id, reason: reason.trim() },
                  {
                    onSuccess: () => {
                      setReasonOpen(false);
                      setReason("");
                    },
                    onError: (err) => fail(err, "Nie udało się zapisać decyzji."),
                  },
                )
              }
            >
              <ThumbsDown size={12} className="mr-1.5 inline" /> Zapisz decyzję
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setReasonOpen(false);
                setError("");
              }}
            >
              Anuluj
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {!declined && !blocked && (
            <Button
              size="sm"
              variant="ghost"
              disabled={!message.lead}
              title={
                message.lead
                  ? "Nasza decyzja — zatrzymuje kampanie, nie trafia na listę sprzeciwów"
                  : "Najpierw wskaż firmę — werdykt dotyczy firmy, nie adresu"
              }
              onClick={() => {
                setError("");
                setReasonOpen(true);
              }}
            >
              <ThumbsDown size={12} className="mr-1.5 inline" /> Nie chcemy
              współpracować
            </Button>
          )}
          {!blocked && (
            <Button
              size="sm"
              variant="ghost"
              loading={blockSender.isPending}
              onClick={() => {
                const what = message.lead_name
                  ? `firmę ${message.lead_name} (NIP i wszystkie jej adresy)`
                  : `adres ${message.from_email}`;
                if (
                  !confirm(
                    `Wpisać ${what} na listę „nie kontaktować"? Trwające kampanie staną.`,
                  )
                )
                  return;
                setError("");
                blockSender.mutate(
                  { id: message.id },
                  { onError: (err) => fail(err, "Nie udało się zapisać zakazu.") },
                );
              }}
              title="Wpisuje na listę „nie kontaktować” i zatrzymuje kampanie"
            >
              <Ban size={12} className="mr-1.5 inline" /> Nie kontaktować
            </Button>
          )}
        </div>
      )}

      {error && <p className="mt-2 text-[11px] text-[var(--color-primary)]">{error}</p>}
    </SectionCard>
  );
}

/** „a@x.pl, b@y.pl" → ["a@x.pl", "b@y.pl"]. Przecinek, średnik albo spacja — człowiek
 *  wkleja adresy tak, jak mu wyszły z Outlooka, a nie tak, jak wygodnie parserowi. */
function splitAddresses(raw: string): string[] {
  return raw
    .split(/[,;\s]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function CopyField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="flex items-center gap-2">
      <span className="section-label w-10 shrink-0">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={hint}
        className="w-full border border-[var(--color-border)] bg-transparent px-2 py-1 text-xs text-[var(--color-text-primary)]"
      />
    </label>
  );
}


/**
 * Wiadomość, której system nie przypisał do żadnej firmy — i jedyna droga, żeby
 * to naprawić.
 *
 * Dopasowanie automatyczne idzie po adresie nadawcy i po naszym tokenie
 * rezygnacji zacytowanym w odpowiedzi. Obie drogi zawodzą, gdy ktoś odpisze
 * z prywatnej skrzynki i skasuje cytat — a wtedy nic nie zatrzyma przypomnień
 * do firmy, która przed chwilą do nas napisała. Dlatego ta karta jest głośna:
 * to nie jest kosmetyczny brak linku, tylko sekwencja, która biegnie dalej.
 */
function UnmatchedCard({ message }: { message: InboundMessage }) {
  const [query, setQuery] = useState("");
  const [error, setError] = useState("");
  const assign = useAssignLead();
  // Szukamy dopiero od trzech znaków: lista firm ma tysiące pozycji, a zapytanie
  // po jednej literze i tak nie mówi nic użytecznego.
  const ready = query.trim().length >= 3;
  const { data: found, isFetching } = useLeads(
    { q: query.trim(), include_blocked: true },
    { enabled: ready },
  );
  const candidates = ready ? (found ?? []).slice(0, 8) : [];

  return (
    <SectionCard title="Niedopasowane do firmy">
      <p className="text-[11px] text-[var(--color-text-secondary)]">
        Ta odpowiedź przyszła z adresu, którego nie ma w żadnej kampanii ani
        kartotece (<span className="font-mono">{message.from_email}</span>).
        Dopóki nie wskażesz firmy, <b>jej sekwencja biegnie dalej</b> — a to
        znaczy, że możemy wysłać przypomnienie komuś, kto już nam odpisał.
      </p>

      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setError("");
        }}
        placeholder="Wpisz nazwę firmy albo NIP…"
        className="mt-2 w-full border border-[var(--color-border)] bg-transparent px-2 py-1 text-xs text-[var(--color-text-primary)]"
      />

      {isFetching && (
        <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">szukam…</p>
      )}
      {candidates.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {candidates.map((lead) => (
            <li key={lead.id}>
              <button
                type="button"
                disabled={assign.isPending}
                onClick={() => {
                  if (!confirm(`Przypisać tę odpowiedź do „${lead.name}"? Zatrzyma to przypomnienia do tej firmy.`))
                    return;
                  assign.mutate(
                    { id: message.id, lead: lead.id },
                    {
                      onError: (err) => {
                        const detail = (
                          err as unknown as {
                            response?: { data?: { errors?: unknown } };
                          }
                        )?.response?.data?.errors;
                        setError(
                          typeof detail === "string"
                            ? detail
                            : "Nie udało się przypisać wiadomości.",
                        );
                      },
                    },
                  );
                }}
                className="flex w-full items-center justify-between gap-2 border border-[var(--color-border)] px-2 py-1 text-left text-xs hover:border-[var(--color-primary)]"
              >
                <span className="truncate text-[var(--color-text-primary)]">{lead.name}</span>
                <span className="shrink-0 font-mono text-[11px] text-[var(--color-text-muted)]">
                  {lead.nip || "bez NIP"}
                </span>
                <Link2 size={12} className="shrink-0 text-[var(--color-text-muted)]" />
              </button>
            </li>
          ))}
        </ul>
      )}
      {ready && !isFetching && candidates.length === 0 && (
        <p className="mt-1 text-[11px] text-[var(--color-text-muted)]">
          Nic nie pasuje. Jeśli tej firmy nie ma w bazie, zostaw wiadomość
          nieprzypisaną — nie ma czego zatrzymywać.
        </p>
      )}
      {error && <p className="mt-1 text-[11px] text-[var(--color-primary)]">{error}</p>}
    </SectionCard>
  );
}
